// Vollea web — shared UI primitives (Btn, Card, Avatar, Logo, Input, Pill, StatBox)
import React from "react";
import { C, SP, R } from "../lib/theme";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_vollea-match/artifacts/bbkllrr6_113445FF-96D8-45A9-AAF7-E1BF1221DD62.png";

export function Logo({ size = 28, style }) {
  return <img src={LOGO_URL} alt="Vollea" style={{ width: size * 2.8, height: size * 2.8, objectFit: "contain", ...style }} />;
}

export function Btn({ label, onClick, variant = "primary", disabled, loading, icon, style, testid, type = "button" }) {
  const bg = variant === "primary" ? C.lime : variant === "dark" ? C.surface3 : "transparent";
  const fg = variant === "primary" ? C.onLime : C.text;
  const border = variant === "outline" ? `1px solid ${C.borderStrong}` : "none";
  return (
    <button
      type={type}
      data-testid={testid}
      onClick={onClick}
      disabled={disabled || loading}
      style={{
        height: 54, borderRadius: R.md, width: "100%", display: "flex", alignItems: "center",
        justifyContent: "center", gap: 8, padding: `0 ${SP.lg}px`, background: bg, border,
        color: fg, fontWeight: 700, fontSize: 15, letterSpacing: 1, opacity: disabled ? 0.45 : 1,
        transition: "opacity .15s ease, transform .1s ease",
        textTransform: "uppercase",
        ...style,
      }}
      onMouseDown={(e) => (e.currentTarget.style.transform = "scale(0.98)")}
      onMouseUp={(e) => (e.currentTarget.style.transform = "scale(1)")}
      onMouseLeave={(e) => (e.currentTarget.style.transform = "scale(1)")}
    >
      {icon}
      {loading ? "…" : label}
    </button>
  );
}

export function Card({ children, style, onClick, testid }) {
  return (
    <div
      data-testid={testid}
      onClick={onClick}
      style={{
        background: C.surface, borderRadius: R.lg, padding: SP.lg,
        border: `1px solid ${C.border}`, cursor: onClick ? "pointer" : "default",
        ...style,
      }}
    >
      {children}
    </div>
  );
}

export function Avatar({ uri, name, size = 44, style }) {
  const initials = (name || "?").split(" ").map((n) => n[0]).filter(Boolean).slice(0, 2).join("").toUpperCase();
  if (uri) return <img src={uri} alt="" style={{ width: size, height: size, borderRadius: size / 2, objectFit: "cover", background: C.surface3, ...style }} />;
  return (
    <div style={{
      width: size, height: size, borderRadius: size / 2, background: C.limeTint,
      display: "flex", alignItems: "center", justifyContent: "center",
      color: C.lime, fontWeight: 700, fontSize: size * 0.36, flexShrink: 0,
      ...style,
    }}>{initials}</div>
  );
}

export function Input({ value, onChange, placeholder, type = "text", testid, style, autoCapitalize, maxLength }) {
  return (
    <input
      data-testid={testid}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      type={type}
      maxLength={maxLength}
      style={{
        width: "100%", background: C.surface2, borderRadius: R.md,
        border: `1px solid ${C.border}`, color: C.text, padding: `0 ${SP.lg}px`,
        height: 52, fontSize: 15, outline: "none",
        textTransform: autoCapitalize === "characters" ? "uppercase" : "none",
        letterSpacing: autoCapitalize === "characters" ? 2 : 0,
        ...style,
      }}
      onFocus={(e) => (e.target.style.borderColor = C.lime)}
      onBlur={(e) => (e.target.style.borderColor = C.border)}
    />
  );
}

export function Pill({ label, active, onClick, icon, testid, style }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      style={{
        height: 36, padding: `0 ${SP.lg}px`, borderRadius: R.pill,
        background: active ? C.lime : C.surface,
        border: `1px solid ${active ? C.lime : C.border}`,
        color: active ? C.onLime : C.textMuted,
        fontWeight: 600, fontSize: 13,
        display: "inline-flex", alignItems: "center", gap: 6, flexShrink: 0,
        ...style,
      }}
    >
      {icon}
      {label}
    </button>
  );
}

export function StatBox({ value, label, accent = false, testid }) {
  return (
    <div data-testid={testid} style={{
      background: C.surface2, borderRadius: R.lg, padding: SP.lg,
      border: `1px solid ${C.border}`, flex: 1,
    }}>
      <div style={{ color: accent ? C.lime : C.text, fontWeight: 700, fontSize: 28, lineHeight: 1 }}>{value}</div>
      <div style={{ color: C.textMuted, fontSize: 12, marginTop: 6, letterSpacing: 0.5 }}>{label}</div>
    </div>
  );
}

export function Header({ title, subtitle, right, onBack, testid }) {
  return (
    <div style={{ padding: `${SP.lg}px ${SP.lg}px ${SP.md}px`, display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: SP.md }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        {onBack && (
          <button onClick={onBack} data-testid="header-back" style={{ color: C.text, marginBottom: SP.sm, padding: 0 }}>
            <span style={{ fontSize: 22 }}>‹ Back</span>
          </button>
        )}
        <h1 data-testid={testid} style={{ color: C.text, fontWeight: 700, fontSize: 28, margin: 0, letterSpacing: 0.5 }}>{title}</h1>
        {subtitle && <p style={{ color: C.textMuted, fontSize: 13, margin: "4px 0 0" }}>{subtitle}</p>}
      </div>
      {right && <div>{right}</div>}
    </div>
  );
}

export function SectionLabel({ children, style }) {
  return (
    <div style={{
      color: C.textMuted, fontSize: 12, fontWeight: 700, letterSpacing: 1.2,
      textTransform: "uppercase", marginBottom: SP.md, ...style,
    }}>
      {children}
    </div>
  );
}

export function Empty({ icon, text, testid }) {
  return (
    <div data-testid={testid} style={{
      display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
      padding: `${SP.xxxl}px ${SP.lg}px`, gap: SP.md, color: C.textDim, textAlign: "center",
    }}>
      {icon}
      <p style={{ color: C.textMuted, fontSize: 14, margin: 0 }}>{text}</p>
    </div>
  );
}
